<template>
    <div class="h-auto w-full min-h-screen flex flex-col items-center justify-center bg-gray-900">
        <!-- Login Section -->
        <div class="text-center mb-2 relative">
            <h2
                class="text-3xl sm:text-4xl lg:text-5xl font-extrabold bg-gradient-to-r from-gray-300 to-white bg-clip-text text-transparent mb-2">
                Welcome Back!
            </h2>
            <p class="text-base sm:text-lg text-gray-300 font-medium">
                Log in to continue your graceful journey.
            </p>
        </div>

        <form
            class="mt-6 w-full max-w-[440px] bg-gray-800/80 backdrop-blur-sm px-6 sm:px-8 py-8 sm:py-10 rounded-2xl shadow-xl border border-gray-700/50 relative z-10"
            @submit.prevent="doLogin">
            <div class="login-form">
                <div class="input-group mb-4">
                    <label for="email" class="flex items-center font-extrabold text-gray-300 mb-2">
                        <svg class="w-4 h-4 mr-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                        </svg>
                        Email Address
                    </label>
                    <input id="email" placeholder="Enter your email" type="email" v-model="email" class="input-field" />
                    <p v-if="emailError" class="error-text mt-1">{{ emailError }}</p>
                </div>

                <div class="input-group mb-4">
                    <label for="password"
                        class="flex items-center font-extrabold text-gray-300 mb-2">
                        <svg class="w-4 h-4 mr-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        Password
                    </label>
                    <input id="password" placeholder="Enter your password" type="password" v-model="password"
                        class="input-field" />
                    <p v-if="passwordError" class="error-text mt-1">{{ passwordError }}</p>
                </div>

                <p v-if="combinedError" class="error-text text-center mb-4">{{ combinedError }}</p>
            </div>

            <button type="submit" :disabled="isLoading.value"
                class="w-full bg-gradient-to-r from-gray-700 to-gray-600 hover:from-gray-600 hover:to-gray-500 disabled:from-gray-800 disabled:to-gray-700 text-white !text-xl py-3 px-4 rounded-xl font-extrabold transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-gray-500/50 transform"
                :class="{ 'animate-pulse': isLoading.value }">
                <span v-if="isLoading.value" class="flex items-center justify-center">
                    <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg"
                        fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4">
                        </circle>
                        <path class="opacity-75" fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                        </path>
                    </svg>
                    Signing in...
                </span>
                <span v-else>Login</span>
            </button>
        </form>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuth } from '../../composables/useAuth';

const email = ref('');
const password = ref('');
const emailError = ref('');
const passwordError = ref('');
const combinedError = ref('');
const router = useRouter();
const { login, isAuthenticated, isLoading } = useAuth();

onMounted(() => {
    if (isAuthenticated.value) {
        router.push('/');
    }
});

const doLogin = async () => {
    emailError.value = '';
    passwordError.value = '';
    combinedError.value = '';

    if (!email.value && !password.value) {
        combinedError.value = 'Email and password are required.';
        return;
    }

    if (!email.value) {
        emailError.value = 'Email is required.';
        return;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value)) {
        emailError.value = 'Enter a valid email address.';
        return;
    }

    if (!password.value) {
        passwordError.value = 'Password is required.';
        return;
    }

    const result = await login(email.value, password.value);

    if (result.success) {
        router.push('/');
    } else {
        combinedError.value = result.error || 'Invalid credentials. Please try again.';
    }
};

</script>

<style scoped>
.login-form {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: auto;
    width: 100%;
}

.input-group {
    width: 100%;
}

.error-text {
    color: #fca5a5;
    font-size: 0.875rem;
    margin-top: 0.25rem;
}

.input-field {
    width: 100%;
    border: 2px solid #4b5563;
    border-radius: 0.75rem;
    padding: 0.875rem 1rem;
    transition: all 0.2s ease-in-out;
    font-size: 1rem;
    background-color: #374151;
    color: white;
}

.input-field:focus {
    outline: none;
    border-color: #6b7280;
    box-shadow: 0 0 0 3px rgba(107, 114, 128, 0.1);
    transform: translateY(-1px);
}

.input-field::placeholder {
    color: #9ca3af;
}

.input-field:focus::placeholder {
    color: #d1d5db;
}
</style>